import React, { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: 'website' | 'article' | 'profile';
  cspPolicy?: string;
}

export const SEO: React.FC<SEOProps> = ({
  title = "ASAP LEARNING | Prepress, Typesetting, JATS XML, Web Development & PDF/UA Accessibility",
  description = "ASAP LEARNING provides automated publishing & web solutions: Web Development Expertise, Adobe InDesign typesetting, JATS 1.3 / BITS 2.1 XML conversion, EPUB3 creation, and 100% compliant PDF/UA accessibility.",
  keywords = "ASAP LEARNING, Web Development Expertise, Custom Publisher Portals, E‑Learning Web Apps, Prepress Automation, Typesetting Services, JATS XML, BITS XML, PDF UA Accessibility, InDesign Automation, STM Journal Publishing, EPUB3 Conversion, Publishing Solutions India",
  canonicalUrl = "https://www.asaplearning.in/",
  ogImage = "https://www.asaplearning.in/og-image.jpg",
  ogType = "website",
  cspPolicy = "default-src 'self'; script-src 'self' 'unsafe-inline' 'unsafe-eval' https:; style-src 'self' 'unsafe-inline' https:; img-src 'self' data: https: blob:; font-src 'self' data: https:; connect-src 'self' https: ws: wss:; frame-src 'self' https:; object-src 'none'; base-uri 'self';"
}) => {
  useEffect(() => {
    // Dynamic Page Title
    document.title = title;

    // Helper to update or create meta tag
    const updateMeta = (name: string, content: string, attribute: 'name' | 'property' | 'http-equiv' = 'name') => {
      let element = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Helper to update or create canonical link tag
    let canonicalElement = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalElement) {
      canonicalElement = document.createElement('link');
      canonicalElement.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalElement);
    }
    canonicalElement.setAttribute('href', canonicalUrl);

    // Standard HTML Meta
    updateMeta('description', description, 'name');
    updateMeta('keywords', keywords, 'name');
    updateMeta('robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1', 'name');
    updateMeta('author', 'ASAP LEARNING Pvt. Ltd.', 'name');

    // Open Graph / Facebook / LinkedIn / WhatsApp
    updateMeta('og:type', ogType, 'property');
    updateMeta('og:site_name', 'ASAP LEARNING', 'property');
    updateMeta('og:title', title, 'property');
    updateMeta('og:description', description, 'property');
    updateMeta('og:url', canonicalUrl, 'property');
    updateMeta('og:image', ogImage, 'property');
    updateMeta('og:image:secure_url', ogImage, 'property');
    updateMeta('og:image:width', '1200', 'property');
    updateMeta('og:image:height', '630', 'property');
    updateMeta('og:image:alt', title, 'property');
    updateMeta('og:locale', 'en_US', 'property');

    // Twitter Card Tags
    updateMeta('twitter:card', 'summary_large_image', 'name');
    updateMeta('twitter:card', 'summary_large_image', 'property');
    updateMeta('twitter:site', '@asaplearning', 'name');
    updateMeta('twitter:creator', '@asaplearning', 'name');
    updateMeta('twitter:title', title, 'name');
    updateMeta('twitter:title', title, 'property');
    updateMeta('twitter:description', description, 'name');
    updateMeta('twitter:description', description, 'property');
    updateMeta('twitter:image', ogImage, 'name');
    updateMeta('twitter:image', ogImage, 'property');

    // Security Meta Tags
    updateMeta('X-Content-Type-Options', 'nosniff', 'http-equiv');
    updateMeta('X-XSS-Protection', '1; mode=block', 'http-equiv');
    updateMeta('referrer', 'strict-origin-when-cross-origin', 'name');

    // Context flags
    const isCareers = canonicalUrl.includes('careers');
    const isSelfPublishing = canonicalUrl.includes('self-publishing') || canonicalUrl.includes('authors') || canonicalUrl.includes('publish');

    // Comprehensive Schema.org Knowledge Graph (AEO, GEO, LLMO, AISEO, E-E-A-T)
    const schemaData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": ["Organization", "EducationalOrganization", "ProfessionalService"],
          "@id": "https://www.asaplearning.in/#organization",
          "name": "ASAP LEARNING",
          "legalName": "ASAP LEARNING Pvt. Ltd.",
          "alternateName": [
            "ASAP Learning Prepress Studio",
            "ASAP Learning Publishing & Web Solutions"
          ],
          "url": "https://www.asaplearning.in/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://www.asaplearning.in/logo.png",
            "caption": "ASAP LEARNING Official Logo"
          },
          "email": "contact@asaplearning.in",
          "telephone": "+91-7817899559",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Dehradun Production Center, Uttam Nagar",
            "addressLocality": "Dehradun",
            "addressRegion": "Uttarakhand",
            "postalCode": "248001",
            "addressCountry": "IN"
          },
          "contactPoint": [
            {
              "@type": "ContactPoint",
              "telephone": "+91-7817899559",
              "contactType": "customer support",
              "email": "contact@asaplearning.in",
              "availableLanguage": ["English", "Hindi"]
            }
          ],
          "sameAs": [
            "https://www.linkedin.com/company/asap-learning-global/"
          ],
          "knowsAbout": [
            "Custom Web Development & Publisher Portals",
            "Adobe InDesign Prepress & Automated Typesetting",
            "JATS 1.3 & BITS 2.1 XML Journal & Book Conversion",
            "PDF/UA (ISO 14289-1) & WCAG 2.1 AA Accessibility Remediation",
            "EPUB 3.2 Reflowable & Fixed Layout E-Book Production",
            "LaTeX & MathType STEM Equation Composition",
            "Primary & Secondary K-12 Textbook Formatting",
            "Higher Education College Courseware Prepress",
            "Author Self-Publishing & Global Book Distribution"
          ],
          "hasCredential": [
            {
              "@type": "EducationalOccupationalCredential",
              "name": "ISO 9001:2015 Quality Management Systems Certification"
            },
            {
              "@type": "EducationalOccupationalCredential",
              "name": "ISO 14289-1 (PDF/UA) Document Accessibility Standard"
            },
            {
              "@type": "EducationalOccupationalCredential",
              "name": "W3C WCAG 2.1 Level AA Accessibility Compliance"
            },
            {
              "@type": "EducationalOccupationalCredential",
              "name": "NISO Z39.96 (JATS 1.3) & NISO Z39.98 (BITS 2.1) Standard Compliance"
            }
          ]
        },
        {
          "@type": "ProfessionalService",
          "@id": "https://www.asaplearning.in/#professionalservice",
          "name": "ASAP LEARNING Prepress & Web Engineering Services",
          "image": "https://www.asaplearning.in/og-image.jpg",
          "url": "https://www.asaplearning.in/",
          "telephone": "+91-7817899559",
          "priceRange": "$$",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Dehradun Production Center, Uttam Nagar",
            "addressLocality": "Dehradun",
            "addressRegion": "Uttarakhand",
            "postalCode": "248001",
            "addressCountry": "IN"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "30.3165",
            "longitude": "78.0039"
          },
          "openingHoursSpecification": {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": [
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday",
              "Saturday"
            ],
            "opens": "09:00",
            "closes": "20:00"
          }
        },
        {
          "@type": "WebSite",
          "@id": "https://www.asaplearning.in/#website",
          "url": "https://www.asaplearning.in/",
          "name": "ASAP LEARNING - Prepress, Typesetting, JATS XML & Web Solutions",
          "description": "Enterprise automated prepress typesetting, JATS/BITS XML conversion, EPUB3, WCAG / PDF/UA accessibility remediation, and custom web app engineering.",
          "publisher": {
            "@id": "https://www.asaplearning.in/#organization"
          },
          "inLanguage": "en-US",
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://www.asaplearning.in/?s={search_term_string}",
            "query-input": "required name=search_term_string"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": `${canonicalUrl}#breadcrumb`,
          "itemListElement": isSelfPublishing ? [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://www.asaplearning.in/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Self-Publishing & Author Studio",
              "item": "https://www.asaplearning.in/#self-publishing"
            }
          ] : isCareers ? [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://www.asaplearning.in/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Careers & Opportunities",
              "item": "https://www.asaplearning.in/#careers"
            }
          ] : [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://www.asaplearning.in/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Publishing & Web Services",
              "item": "https://www.asaplearning.in/#services"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": "About ASAP LEARNING",
              "item": "https://www.asaplearning.in/#about"
            },
            {
              "@type": "ListItem",
              "position": 4,
              "name": "Contact HQ",
              "item": "https://www.asaplearning.in/#contact"
            }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://www.asaplearning.in/#service-webdev",
          "name": "Web Development Expertise & Publisher Portals",
          "serviceType": "Enterprise Full-Stack Web App Engineering",
          "provider": { "@id": "https://www.asaplearning.in/#organization" },
          "description": "Custom enterprise web applications, author submission portals, e-learning platforms, and cloud API pipelines built with React, Next.js, and Node.js.",
          "offers": {
            "@type": "Offer",
            "priceCurrency": "USD",
            "price": "Contact for custom quote",
            "availability": "https://schema.org/InStock"
          }
        },
        {
          "@type": "Service",
          "@id": "https://www.asaplearning.in/#service-typesetting",
          "name": "Adobe InDesign Typesetting & Prepress Composition",
          "serviceType": "Typesetting & Layout Engineering",
          "provider": { "@id": "https://www.asaplearning.in/#organization" },
          "description": "High-precision page composition for K-12 textbooks, higher ed courseware, academic journals, and technical manuals with 100% baseline grid precision.",
          "offers": {
            "@type": "Offer",
            "priceCurrency": "USD",
            "availability": "https://schema.org/InStock"
          }
        },
        {
          "@type": "Service",
          "@id": "https://www.asaplearning.in/#service-xml",
          "name": "JATS 1.3 & BITS 2.1 XML Digital Publishing",
          "serviceType": "XML Conversion & DTD Validation",
          "provider": { "@id": "https://www.asaplearning.in/#organization" },
          "description": "Structured XML conversion complying with NISO Z39.96 (JATS) and NISO Z39.98 (BITS) standards for PubMed Central and Crossref indexing.",
          "offers": {
            "@type": "Offer",
            "priceCurrency": "USD",
            "availability": "https://schema.org/InStock"
          }
        },
        {
          "@type": "Service",
          "@id": "https://www.asaplearning.in/#service-accessibility",
          "name": "PDF/UA & WCAG 2.1 AA Document Accessibility Remediation",
          "serviceType": "Accessibility Remediation Services",
          "provider": { "@id": "https://www.asaplearning.in/#organization" },
          "description": "100% compliant PDF/UA (ISO 14289-1) and WCAG 2.1 Level AA document tagging, screen reader optimization, and alt-text authoring.",
          "offers": {
            "@type": "Offer",
            "priceCurrency": "USD",
            "availability": "https://schema.org/InStock"
          }
        },
        {
          "@type": "FAQPage",
          "@id": "https://www.asaplearning.in/#faqpage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How does ASAP LEARNING combine AI with traditional typesetting precision?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "ASAP LEARNING leverages AI-assisted automated workflow engines to ingest raw manuscripts, parse structural styles, and auto-populate templates in InDesign and LaTeX. Experienced human typographers then review typography, pagination, baseline grids, and line-breaks to guarantee 100% publisher-grade accuracy."
              }
            },
            {
              "@type": "Question",
              "name": "What quality control measures ensure error-free XML and PDF deliverables?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "ASAP LEARNING's multi-tier QA protocol combines automated schema validators (BITS, JATS, DocBook, EPUB3) with rigorous manual proofing. Every equation, reference citation, and table is cross-checked against strict STM guidelines before final delivery."
              }
            },
            {
              "@type": "Question",
              "name": "What is the typical turnaround time for complex academic or technical books?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Thanks to automated prepress pipelines, standard turnaround times are reduced by up to 60%. A typical 300-page academic book or journal volume is completed within 3 to 7 business days, with express 24-48 hour turnaround available for urgent releases."
              }
            },
            {
              "@type": "Question",
              "name": "Which input and output formats are supported for digital and print publishing?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Input formats include MS Word (.docx), TeX/LaTeX, PDF, and raw text. Output deliverables include press-ready PDF/X-1a and PDF/X-4, reflowable & fixed-layout EPUB3, accessible PDF/UA (ISO 14289-1), JATS 1.3 / BITS 2.1 XML, HTML5, and custom structured XML."
              }
            },
            {
              "@type": "Question",
              "name": "What custom web development services does ASAP LEARNING offer?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "ASAP LEARNING builds custom enterprise web portals, author manuscript submission platforms, interactive e-learning web applications, publisher admin dashboards, and cloud API integrations using React, Next.js, and Node.js."
              }
            },
            {
              "@type": "Question",
              "name": "How are project quotes calculated at ASAP LEARNING?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Pricing is based on page count, complexity (such as heavy math equations, complex tables, or multilingual formatting), and required output formats. A free 10-page test sample is available upon request."
              }
            }
          ]
        },
        {
          "@type": "HowTo",
          "@id": "https://www.asaplearning.in/#howto-prepress-pipeline",
          "name": "How ASAP LEARNING Converts Manuscripts into Validated JATS XML & Press-Ready PDF",
          "description": "The 5-step automated and human-verified prepress workflow used by ASAP LEARNING for publisher clients.",
          "step": [
            {
              "@type": "HowToStep",
              "position": 1,
              "name": "Scope Intake & File Audit",
              "text": "Manuscripts and design briefs undergo automated structural preflight analysis and font extraction."
            },
            {
              "@type": "HowToStep",
              "position": 2,
              "name": "DTD & Style Mapping",
              "text": "Technical specialists map styling guidelines, DTD rules (JATS/BITS), and page grid layouts."
            },
            {
              "@type": "HowToStep",
              "position": 3,
              "name": "AI-Assisted Typesetting & Conversion",
              "text": "InDesign composition and automated XML conversion engines perform page layout balancing and structural tagging."
            },
            {
              "@type": "HowToStep",
              "position": 4,
              "name": "Multi-Tier Quality Validation",
              "text": "100% proofing, PAC 2021 accessibility checks, and schema DTD linter verification guarantee zero defects."
            },
            {
              "@type": "HowToStep",
              "position": 5,
              "name": "Final Delivery & Archival Packaging",
              "text": "Press-ready PDF/X files, package INDD sources, and validated JATS XML repositories are delivered securely."
            }
          ]
        }
      ]
    };

    let scriptTag = document.querySelector('script[type="application/ld+json"]') as HTMLScriptElement;
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.setAttribute('type', 'application/ld+json');
      document.head.appendChild(scriptTag);
    }
    scriptTag.textContent = JSON.stringify(schemaData);

  }, [title, description, keywords, canonicalUrl, ogImage, ogType]);

  return null;
};
